import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import NewPageTitleBar from '@/components/shared/NewPageTitleBar';
import FilterDayDetail, { DayDetailData } from '@/components/filters/FilterDayDetail';
import AppCard from '@/components/appcard/AppCard';
import AppSectionLoader from '@/components/AppSectionLoader';
import WcnApexchart from '@/components/charts/WcnApexchart';
import { getMenuData } from '@/core/data/menuData';
import { useNotifications } from '@/core/hooks/useNotifications';
import { apiClient } from '@core/api';
import { FCSecurePage, useRegisterRefresh } from '@/components/shared/navigation/securePage.ts';
import { FilterRef } from '@features/statistics/types.ts';

interface ThresholdData {
    name: string;
    value: number;
}

interface FillLevelData {
    intervalStart: string;
    max: number;
    average: number;
    min: number;
}

interface SeriesData {
    name: string;
    data: Array<{ x: string; y: number }>;
}

const TrolleyShelterDailyDetails: FCSecurePage = () => {
    const { t } = useTranslation();
    const { notifyErrorResponse } = useNotifications();

    const [loading, setLoading] = useState(false);
    const [series, setSeries] = useState<SeriesData[]>([]);
    const [lastUpdate, setLastUpdate] = useState<DateTime | undefined>(undefined);
    const filterRef = useRef<FilterRef>(null);

    const loadChartData = async (args: DayDetailData) => {
        try {
            setLoading(true);

            const [shelterThresholds, shelterFillLevels] = await Promise.all([
                apiClient.get<ThresholdData[]>(`trolleyshelter/${args.deviceId}/${args.moduleId}/threshold`),
                apiClient.get<FillLevelData[]>(
                    `statistics/shelterfilllevel/${args.deviceId}/${args.moduleId}/${args.start}/${args.end}/${args.interval}`
                )
            ]);

            const seriesData: SeriesData[] = [
                { name: 'Max', data: [] },
                { name: t('average'), data: [] },
                { name: 'Min', data: [] }
            ];

            shelterThresholds.data.forEach((t) => {
                seriesData.push({ name: t.name, data: [] });
            });

            shelterFillLevels.data.forEach((element) => {
                const intervalStart = DateTime.fromISO(element.intervalStart).toString();
                seriesData[0].data.push({ x: intervalStart, y: element.max });
                seriesData[1].data.push({ x: intervalStart, y: element.average });
                seriesData[2].data.push({ x: intervalStart, y: element.min });
                for (let i = 0; i < shelterThresholds.data.length; i += 1) {
                    seriesData[3 + i].data.push({
                        x: intervalStart,
                        y: shelterThresholds.data[i].value
                    });
                }
            });

            setLastUpdate(DateTime.now());
            setLoading(false);

            setSeries(seriesData);
        } catch (error) {
            notifyErrorResponse(error);
        } finally {
            setLoading(false);
        }
    };

    const handleReload = useCallback(async () => {
        filterRef.current?.refresh();
    }, []);

    useRegisterRefresh(handleReload, loading);

    return (
        <div>
            <NewPageTitleBar
                icon={<i className="zmdi zmdi-cast zmdi-hc-rotate-180" style={{ fontSize: '16px' }} />}
                title={`${t('trolleyShelter')} ${t('dailyDetails')}`}
                menus={getMenuData()}
                iconSize="18px"
                currentRoute={{ path: '/statistics/rfid/dailydetails' }}
                lastUpdate={lastUpdate}
                t={t}
            />

            <div className="container grid-list-xl container--fluid no-padding-top">
                <FilterDayDetail ref={filterRef} forType="trolleyShelter" onLoadData={loadChartData} />
                <div className="row">
                    <AppSectionLoader status={loading} />
                    <AppCard
                        heading={t('fillLevel')}
                        icon="cast"
                        iconClass="rotate-180"
                        colClasses="col-spacing"
                        customClasses="no-padding-bottom"
                        fullBlock={true}
                        footer={false}
                    >
                        <WcnApexchart
                            type="line"
                            series={series}
                            height={480}
                            yAxisMin={0}
                            chartId="dailyDetailsCharts"
                            xAxisType="datetime"
                            strokeDashArray={[0, 0, 0, 10, 10, 10]}
                        />
                    </AppCard>
                </div>
            </div>
        </div>
    );
};

export default TrolleyShelterDailyDetails;
